import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';

interface Props {
  hospitalId: string;
  /** Open the section automatically on mount (e.g., when it's the only thing
   *  occupying a sidebar column and the surrounding context already explains
   *  why it's there). Defaults to false — collapsed with a teaser blurb. */
  defaultExpanded?: boolean;
}

const PAGE_SIZE = 100;

interface CmRow {
  id: string;
  chargemaster_code: string | null;
  chargemaster_description: string | null;
  gross_charge: number | null;
  cpt_code: string | null;
  hcpcs_code: string | null;
  drg_code: string | null;
  revenue_code: string | null;
  ndc_code: string | null;
  supply_code: string | null;
  drug_code: string | null;
  data_error_reason: string | null;
}

interface ExcludedProc {
  code: string;
  code_type: string;
  code_status: string;
  description: string | null;
}

// Pull the most-specific code + label from a chargemaster row.
function pickCode(r: CmRow): { code: string; type: string } {
  if (r.cpt_code) return { code: r.cpt_code, type: 'CPT' };
  if (r.hcpcs_code) return { code: r.hcpcs_code, type: 'HCPCS' };
  if (r.drg_code) return { code: r.drg_code, type: 'MS-DRG' };
  if (r.revenue_code) return { code: r.revenue_code, type: 'REV' };
  if (r.ndc_code || r.drug_code) return { code: r.ndc_code || r.drug_code || '', type: 'NDC' };
  if (r.supply_code) return { code: r.supply_code, type: 'Supply' };
  return { code: r.chargemaster_code || '—', type: 'Other' };
}

// Translate the raw data_error_reason tag into a one-liner a non-engineer
// can act on. Long unit-mismatch narratives get passed through verbatim
// since they're already written for human readers. When the validation
// pipeline flagged a row but didn't store a narrative (~85% of cases),
// fall back to a price-magnitude heuristic so the user still gets a hint.
function reasonLabel(raw: string | null, gross: number | null, codeType: string): { short: string; detail: string | null } {
  if (raw === 'absurd_magnitude') {
    return {
      short: 'magnitude flag',
      detail: 'Validation rule flagged this charge as far outside the typical range for this code. May indicate an encoding mistake (wrong code applied to a routine line) or a unit-quantity normalization error in the import — needs human review before treating it as a definitive error.',
    };
  }
  if (raw === 'implausible_for_code_type') {
    return {
      short: 'code-type mismatch',
      detail: 'Price band looks wrong for the code type filed (e.g., a routine CPT priced like an inpatient stay). Pipeline flagged for review.',
    };
  }
  if (raw) {
    // Verbose unit-mismatch / unit-normalization narratives — show as-is.
    return { short: raw.slice(0, 36), detail: raw };
  }
  // No stored narrative — be honest about that. We deliberately do NOT
  // try to guess the reason from price magnitude alone: gene therapies
  // legitimately cost $3-4M per dose (Hemgenix, Beqvez, Lenmeldy,
  // Elevidys, etc.), so a $4M HCPCS J-code is often the correct price,
  // not an error. Inferring "implausible" from a high price would
  // mis-label real prices as data errors.
  if (gross != null && gross < 2) {
    return {
      short: 'placeholder price',
      detail: 'Price is under $2 — almost always a placeholder, test row, or sentinel value rather than a real charge.',
    };
  }
  return {
    short: 'flagged in validation',
    detail: 'Validation pipeline marked this row as a likely error but did not store a specific reason. May be a real encoding mistake (price/code mismatch, unit normalization issue) — or a false positive that needs review.',
  };
}

export function HospitalExcludedCodesSection({ hospitalId, defaultExpanded = false }: Props) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [page, setPage] = useState(0);

  // Lightweight count for the badge. Excludes 'superseded_by_reimport' —
  // these are old chargemaster rows from a prior import, not validation
  // errors. Older import scripts soft-deleted via is_data_error=true,
  // dwarfing the real flagged rows ~6:1 on average and confusing the UI.
  const { data: dataErrorCount } = useQuery({
    queryKey: ['excluded-codes-count', hospitalId],
    queryFn: async () => {
      const { count } = await supabase
        .from('chargemaster_mappings')
        .select('id', { count: 'exact', head: true })
        .eq('hospital_id', hospitalId)
        .eq('is_data_error', true)
        .or('data_error_reason.is.null,data_error_reason.neq.superseded_by_reimport');
      return count ?? 0;
    },
    enabled: expanded,
  });

  // Globally-excluded codes (NOC catch-alls etc.) — small fixed list, ~57 rows.
  const { data: excludedProcs } = useQuery({
    queryKey: ['excluded-procedures-global'],
    queryFn: async (): Promise<ExcludedProc[]> => {
      const { data } = await supabase
        .from('procedures')
        .select('code, code_type, code_status, description')
        .neq('code_status', 'active');
      return (data ?? []) as ExcludedProc[];
    },
    enabled: expanded,
    staleTime: 60 * 60 * 1000, // 1 hour — this list rarely changes
  });

  const cptExcl = useMemo(() => excludedProcs?.filter(p => p.code_type === 'CPT').map(p => p.code) ?? [], [excludedProcs]);
  const hcpcsExcl = useMemo(() => excludedProcs?.filter(p => p.code_type === 'HCPCS').map(p => p.code) ?? [], [excludedProcs]);
  const revExcl = useMemo(() => excludedProcs?.filter(p => p.code_type === 'REV').map(p => p.code) ?? [], [excludedProcs]);

  // Page of data-error rows. Same superseded_by_reimport filter as the
  // count query — without it, the table is dominated by old chargemaster
  // rows from prior imports rather than actual validation findings.
  const { data: errorRows, isLoading: errorLoading } = useQuery({
    queryKey: ['excluded-codes-error-rows', hospitalId, page],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('chargemaster_mappings')
        .select('id, chargemaster_code, chargemaster_description, gross_charge, cpt_code, hcpcs_code, drg_code, revenue_code, ndc_code, supply_code, drug_code, data_error_reason')
        .eq('hospital_id', hospitalId)
        .eq('is_data_error', true)
        .or('data_error_reason.is.null,data_error_reason.neq.superseded_by_reimport')
        .order('gross_charge', { ascending: false, nullsFirst: false })
        .range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1);
      if (error) throw error;
      return (data ?? []) as CmRow[];
    },
    enabled: expanded,
  });

  // Rows tied to globally-excluded codes (NOC etc.). Only fetch on page 0.
  // 3 separate parallel queries — Supabase's .or() chokes when an inner
  // `in.(a,b,c)` filter contains commas because the OR string parser splits
  // on every comma without respecting parentheses. Fan out + concat instead.
  const { data: noErrorExcludedRows } = useQuery({
    queryKey: ['excluded-codes-noc-rows', hospitalId, cptExcl.join(','), hcpcsExcl.join(','), revExcl.join(',')],
    queryFn: async () => {
      const fetches: Promise<CmRow[]>[] = [];
      const cols = 'id, chargemaster_code, chargemaster_description, gross_charge, cpt_code, hcpcs_code, drg_code, revenue_code, ndc_code, supply_code, drug_code, data_error_reason';
      const fanout = (col: 'cpt_code' | 'hcpcs_code' | 'revenue_code', codes: string[]) => {
        if (!codes.length) return;
        fetches.push((async () => {
          const { data, error } = await supabase
            .from('chargemaster_mappings')
            .select(cols)
            .eq('hospital_id', hospitalId)
            .eq('is_data_error', false)
            .in(col, codes)
            .order('gross_charge', { ascending: false, nullsFirst: false })
            .limit(50);
          if (error) throw error;
          return (data ?? []) as CmRow[];
        })());
      };
      fanout('cpt_code', cptExcl);
      fanout('hcpcs_code', hcpcsExcl);
      fanout('revenue_code', revExcl);
      const results = await Promise.all(fetches);
      // Merge, dedupe by id, sort by gross_charge desc, cap at 50.
      const seen = new Set<string>();
      const merged: CmRow[] = [];
      for (const list of results) {
        for (const r of list) {
          if (r.id && seen.has(r.id)) continue;
          if (r.id) seen.add(r.id);
          merged.push(r);
        }
      }
      merged.sort((a, b) => (Number(b.gross_charge) || 0) - (Number(a.gross_charge) || 0));
      return merged.slice(0, 50);
    },
    enabled: expanded && page === 0 && !!excludedProcs && (cptExcl.length + hcpcsExcl.length + revExcl.length > 0),
    staleTime: 5 * 60 * 1000,
  });

  const procByKey = useMemo(() => {
    const m = new Map<string, ExcludedProc>();
    for (const p of excludedProcs ?? []) m.set(`${p.code_type}:${p.code}`, p);
    return m;
  }, [excludedProcs]);

  const totalErrors = dataErrorCount ?? 0;
  const totalNocRows = noErrorExcludedRows?.length ?? 0;
  const isFirstPage = page === 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">
          <button
            type="button"
            className="flex items-center gap-2 w-full text-left hover:opacity-80"
            onClick={() => setExpanded(v => !v)}
          >
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <span>Excluded from rankings</span>
            {expanded && (totalErrors > 0 || totalNocRows > 0) && (
              <Badge variant="outline" className="ml-2 text-xs">
                {(totalErrors + (isFirstPage ? totalNocRows : 0)).toLocaleString()} rows
              </Badge>
            )}
          </button>
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1 pl-6">
          Chargemaster lines we couldn't fold into this hospital's ranking metrics. Two
          buckets: <strong>flagged in validation</strong> (the import pipeline marked the row
          as a likely encoding mistake — price/code mismatch, unit normalization issue, etc. —
          and most of these need human review before we'd call them definitive errors) and
          <strong> excluded codes</strong> (catch-all "not otherwise classified" codes that
          we exclude globally because they cover wildly different things across hospitals and
          have no consistent national benchmark to compare against).
        </p>
      </CardHeader>
      {expanded && (
        <CardContent>
          {errorLoading ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
          ) : (
            <>
              <div className="overflow-x-auto rounded border border-border/60">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50 border-b">
                    <tr className="text-left text-muted-foreground">
                      <th className="px-2 py-1.5 font-medium">Type</th>
                      <th className="px-2 py-1.5 font-medium">Code</th>
                      <th className="px-2 py-1.5 font-medium">Description (as listed by hospital)</th>
                      <th className="px-2 py-1.5 font-medium text-right">Gross charge</th>
                      <th className="px-2 py-1.5 font-medium">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Data-error rows first — these surface the actionable signal
                        (e.g., gene-therapy code at aspirin price). NOC catch-all
                        rows are rendered after, capped at 50, since they're
                        expected for any hospital that uses NOC codes. */}
                    {(errorRows ?? []).map((r, i) => {
                      const { code, type } = pickCode(r);
                      const reason = reasonLabel(r.data_error_reason, r.gross_charge, type);
                      return (
                        <tr key={`err-${r.id ?? i}`} className="border-b border-border/40 hover:bg-muted/30">
                          <td className="px-2 py-1.5"><Badge variant="outline" className="text-[10px]">{type}</Badge></td>
                          <td className="px-2 py-1.5 font-mono">{code}</td>
                          <td className="px-2 py-1.5 max-w-md truncate" title={r.chargemaster_description || ''}>{r.chargemaster_description || <span className="text-muted-foreground italic">no description</span>}</td>
                          <td className="px-2 py-1.5 text-right font-mono tabular-nums">{r.gross_charge != null ? `$${Number(r.gross_charge).toLocaleString()}` : '—'}</td>
                          <td className="px-2 py-1.5">
                            <Badge variant="destructive" className="text-[10px]">{reason.short}</Badge>
                            {reason.detail && (
                              <div className="text-muted-foreground mt-0.5 text-[10px] max-w-md">{reason.detail}</div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {isFirstPage && (noErrorExcludedRows ?? []).map((r, i) => {
                      const { code, type } = pickCode(r);
                      const key = `${type === 'MS-DRG' ? 'DRG' : type}:${code}`;
                      const proc = procByKey.get(key);
                      return (
                        <tr key={`exc-${r.id ?? i}`} className="border-b border-border/40 hover:bg-muted/30">
                          <td className="px-2 py-1.5"><Badge variant="outline" className="text-[10px]">{type}</Badge></td>
                          <td className="px-2 py-1.5 font-mono">{code}</td>
                          <td className="px-2 py-1.5 max-w-md truncate" title={r.chargemaster_description || ''}>{r.chargemaster_description || <span className="text-muted-foreground italic">no description</span>}</td>
                          <td className="px-2 py-1.5 text-right font-mono tabular-nums">{r.gross_charge != null ? `$${Number(r.gross_charge).toLocaleString()}` : '—'}</td>
                          <td className="px-2 py-1.5">
                            <Badge variant="secondary" className="text-[10px]">excluded code</Badge>
                            <div className="text-muted-foreground mt-0.5 text-[10px] max-w-md">{proc?.description || `Code is globally ${proc?.code_status ?? 'excluded'}`}</div>
                          </td>
                        </tr>
                      );
                    })}
                    {totalErrors === 0 && totalNocRows === 0 && (
                      <tr>
                        <td colSpan={5} className="px-2 py-6 text-center text-muted-foreground">
                          No excluded rows for this hospital — every line in their chargemaster passed validation.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {totalErrors > PAGE_SIZE && (
                <div className="flex items-center justify-between mt-3 text-xs">
                  <span className="text-muted-foreground">
                    Showing data-error rows {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalErrors)} of {totalErrors.toLocaleString()}
                  </span>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>← Prev</Button>
                    <Button size="sm" variant="outline" disabled={(page + 1) * PAGE_SIZE >= totalErrors} onClick={() => setPage(p => p + 1)}>Next →</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
}
